"use client";

import { useState, useEffect } from "react";
import { ArrowLeft, Bot, ChevronRight, Star, Volume2, Lock, Newspaper, HardDrive, MessageCircle, FileText } from "lucide-react";
import { Button } from "@/shadcn-components/ui/button";
import { Card, CardContent } from "@/shadcn-components/ui/card";
import { Switch } from "@/shadcn-components/ui/switch";
import { Label } from "@/shadcn-components/ui/label";
import { Badge } from "@/shadcn-components/ui/badge";
import { Separator } from "@/shadcn-components/ui/separator";
import { useSettingsStore } from "@/lib/settings";
import {
  APP_DISPLAY_VERSION,
  APP_DISPLAY_BUILD_LABEL,
  APP_DISPLAY_PLATFORM,
  formatAppBuildTime,
} from "@/lib/app-version";
import { VybeLogo } from "@/components/common/VybeLogo";

// Import screen components
import StorageScreen from "./StorageScreen";
import ContactDeveloperScreen from "./ContactDeveloperScreen";

interface SettingsDetailsContentProps {
  onBack: () => void;
}

export function SettingsDetailsContent({ onBack }: SettingsDetailsContentProps) {
  const { settings, updateSetting, loadSettings } = useSettingsStore();
  const [currentView, setCurrentView] = useState<"main" | "storage" | "contact">("main");

  // Load settings on mount
  useEffect(() => {
    loadSettings();
  }, []);

  const toggleSounds = () => {
    updateSetting("sound", !settings.sound);
  };

  const toggleAutoLock = () => {
    updateSetting("autoLock", !settings.autoLock);
  };

  const toggleEnableNews = () => {
    updateSetting("newsEnabled", !settings.newsEnabled);
  };

  const toggleAgentTrading = () => {
    updateSetting("agentTradingConsent", !settings.agentTradingConsent);
  };

  // History detail depth: off = compact MT5-style card, on = every raw field.
  const toggleDealDetailsExtended = () => {
    updateSetting("dealDetailsExtended", !settings.dealDetailsExtended);
  };

  const handleShowStorage = () => {
    setCurrentView("storage");
  };

  const handleShowContactDeveloper = () => {
    setCurrentView("contact");
  };

  const handleBackToMain = () => {
    setCurrentView("main");
  };

  const renderMainView = () => (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="px-6 py-6 border-b border-border">
        <div className="flex items-center gap-3">
          <Button
            variant="ghost"
            size="icon"
            onClick={onBack}
            className="h-8 w-8"
          >
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <div className="space-y-1">
            <h2 className="text-lg font-semibold">Settings</h2>
            <p className="text-sm text-muted-foreground">
              General application settings
            </p>
          </div>
        </div>
      </div>

      {/* Settings Content */}
      <div className="flex-1 overflow-y-auto px-6 py-4">
        <div className="space-y-6">
          {/* General Settings */}
          <div>
            <h3 className="text-muted-foreground text-sm uppercase font-medium tracking-wide mb-3">
              GENERAL
            </h3>
            <Card>
              <CardContent className="p-0">
                {/* Sounds Toggle */}
                <div className="flex items-center justify-between p-3">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-lg bg-blue-500 flex items-center justify-center">
                      <Volume2 className="text-white text-base" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <Label htmlFor="sounds" className="text-base font-medium cursor-pointer">
                        Sounds
                      </Label>
                      <p className="text-sm text-muted-foreground">
                        Enable notification sounds
                      </p>
                    </div>
                  </div>
                  <Switch
                    id="sounds"
                    checked={settings.sound}
                    onCheckedChange={toggleSounds}
                  />
                </div>

                <Separator />

                {/* Auto Lock Toggle */}
                <div className="flex items-center justify-between p-3">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-lg bg-orange-500 flex items-center justify-center">
                      <Lock className="text-white text-base" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <Label htmlFor="autolock" className="text-base font-medium cursor-pointer">
                        Auto Lock
                      </Label>
                      <p className="text-sm text-muted-foreground">
                        Automatically lock after inactivity
                      </p>
                    </div>
                  </div>
                  <Switch
                    id="autolock"
                    checked={settings.autoLock}
                    onCheckedChange={toggleAutoLock}
                  />
                </div>

                <Separator />

                {/* News Toggle */}
                <div className="flex items-center justify-between p-3">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-lg bg-green-500 flex items-center justify-center">
                      <Newspaper className="text-white text-base" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <Label htmlFor="news" className="text-base font-medium cursor-pointer">
                        Enable News
                      </Label>
                      <p className="text-sm text-muted-foreground">
                        Show financial news updates
                      </p>
                    </div>
                  </div>
                  <Switch
                    id="news"
                    checked={settings.newsEnabled}
                    onCheckedChange={toggleEnableNews}
                  />
                </div>

                <Separator />

                {/* Agent Trading Toggle */}
                <div className="flex items-center justify-between p-3">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-lg bg-red-500 flex items-center justify-center">
                      <Bot className="text-white text-base" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <Label htmlFor="agent-trading" className="text-base font-medium cursor-pointer">
                        Agent Trading
                      </Label>
                      <p className="text-sm text-muted-foreground">
                        Allow the Vybe Agent to place live orders
                      </p>
                    </div>
                  </div>
                  <Switch
                    id="agent-trading"
                    checked={settings.agentTradingConsent}
                    onCheckedChange={toggleAgentTrading}
                  />
                </div>

                <Separator />

                {/* History detail depth (SR Global request 2026-09-08) */}
                <div className="flex items-center justify-between p-3">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-lg bg-slate-500 flex items-center justify-center">
                      <FileText className="text-white text-base" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <Label htmlFor="deal-details-extended" className="text-base font-medium cursor-pointer">
                        Extended history details
                      </Label>
                      <p className="text-sm text-muted-foreground">
                        Off shows the compact card; on shows every raw field
                      </p>
                    </div>
                  </div>
                  <Switch
                    id="deal-details-extended"
                    checked={settings.dealDetailsExtended}
                    onCheckedChange={toggleDealDetailsExtended}
                  />
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Tools & Support */}
          <div>
            <h3 className="text-muted-foreground text-sm uppercase font-medium tracking-wide mb-3">
              TOOLS & SUPPORT
            </h3>
            <Card>
              <CardContent className="p-0">
                {/* Storage */}
                <Button
                  variant="ghost"
                  onClick={handleShowStorage}
                  className="w-full h-auto p-3 justify-start hover:bg-muted/50 rounded-none rounded-t-lg"
                >
                  <div className="w-8 h-8 rounded-lg bg-purple-500 flex items-center justify-center mr-3">
                    <HardDrive className="text-white text-base" />
                  </div>
                  <div className="flex-1 text-left min-w-0 mr-2">
                    <div className="text-sm font-medium">Storage</div>
                    <div className="text-xs text-muted-foreground">
                      Manage app data and cache
                    </div>
                  </div>
                  <ChevronRight className="h-4 w-4 text-muted-foreground" />
                </Button>

                <Separator />

                {/* Contact Developer */}
                <Button
                  variant="ghost"
                  onClick={handleShowContactDeveloper}
                  className="w-full h-auto p-3 justify-start hover:bg-muted/50 rounded-none rounded-b-lg"
                >
                  <div className="w-8 h-8 rounded-lg bg-indigo-500 flex items-center justify-center mr-3">
                    <MessageCircle className="text-white text-base" />
                  </div>
                  <div className="flex-1 text-left min-w-0 mr-2">
                    <div className="text-sm font-medium">Contact Developer</div>
                    <div className="text-xs text-muted-foreground">
                      Get help and support
                    </div>
                  </div>
                  <ChevronRight className="h-4 w-4 text-muted-foreground" />
                </Button>
              </CardContent>
            </Card>
          </div>

          {/* About */}
          <div>
            <h3 className="text-muted-foreground text-sm uppercase font-medium tracking-wide mb-3">
              ABOUT
            </h3>
            <Card>
              <CardContent className="p-4">
                <div className="flex items-center gap-3 mb-4">
                  <VybeLogo className="h-12 w-12" decorative />
                  <div>
                    <h4 className="font-semibold">VybeTrader</h4>
                    <p className="text-sm text-muted-foreground">{APP_DISPLAY_VERSION}</p>
                  </div>
                </div>
                <div className="space-y-2 text-sm text-muted-foreground">
                  <p>The first AI-focused trading platform</p>
                  <div className="flex items-center gap-2">
                    <Star className="h-3 w-3 text-yellow-500" />
                    <span className="text-xs">Built with cutting-edge technology</span>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* App Info */}
          <div>
            <h3 className="text-muted-foreground text-sm uppercase font-medium tracking-wide mb-3">
              APPLICATION INFO
            </h3>
            <Card>
              <CardContent className="p-3">
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Build</span>
                    <span>{APP_DISPLAY_BUILD_LABEL}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Platform</span>
                    <span>{APP_DISPLAY_PLATFORM}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Last Updated</span>
                    <span>{formatAppBuildTime()}</span>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <div className="h-full relative overflow-hidden">
      {/* Main View */}
      <div className={`absolute inset-0 transition-transform duration-300 ease-in-out ${
        currentView === "main" ? "translate-x-0" : "-translate-x-full"
      }`}>
        {renderMainView()}
      </div>

      {/* Storage View */}
      <div className={`absolute inset-0 transition-transform duration-300 ease-in-out ${
        currentView === "storage" ? "translate-x-0" : "translate-x-full"
      }`}>
        {currentView === "storage" && (
          <StorageScreen onBack={handleBackToMain} />
        )}
      </div>

      {/* Contact Developer View */}
      <div className={`absolute inset-0 transition-transform duration-300 ease-in-out ${
        currentView === "contact" ? "translate-x-0" : "translate-x-full"
      }`}>
        {currentView === "contact" && (
          <ContactDeveloperScreen onBack={handleBackToMain} />
        )}
      </div>
    </div>
  );
}